// Scroll-driven reveal and parallax for the Parallax grid section.
//
// Writes three sets of custom properties; the CSS maps them onto each item:
//   --parallax-grid-progress (0 → 1)  the reveal: fade in and travel into place
//   --parallax-grid-scroll   (0 → 1)  the drift: how far through the viewport
//                                      the element is, 0.5 = centred. Items keep
//                                      moving at their own depth after the reveal.
//   --pg-mouse-x / -y        (-1 → 1) pointer position over the section
//                                      (desktop, fine pointers only)
//
// Values are not written straight from the scroll position: each frame they
// ease a fraction of the way towards it (a lerp), so the motion trails the
// scroll and settles instead of stopping dead. Because everything follows the
// scroll position, scrolling back up plays it in reverse.
//
// Desktop: one set of values for the whole section — reveal is 0 when its top
// enters the bottom of the viewport, 1 when its top reaches the top.
// Mobile: the section is taller than the screen, so each item gets its own
// values based on where it sits in the viewport — they reveal one after
// another.

const DESKTOP_QUERY = '(min-width: 1024px)';
const POINTER_QUERY = '(hover: hover) and (pointer: fine)';
const PROGRESS_PROPERTY = '--parallax-grid-progress';
const SCROLL_PROPERTY = '--parallax-grid-scroll';

// Share of the remaining distance covered per 60fps frame. Lower is floatier.
const SCROLL_SMOOTHING = 0.1;
const MOUSE_SMOOTHING = 0.06;
const SETTLED = 0.0005;

const clamp = (value) => Math.min(Math.max(value, 0), 1);

// A value that eases towards its target, frame-rate independent.
class Smoothed {
  constructor(value = 0) {
    this.current = value;
    this.target = value;
  }

  step(smoothing, frames) {
    const delta = this.target - this.current;
    if (Math.abs(delta) < SETTLED) {
      this.current = this.target;
      return false;
    }
    this.current += delta * (1 - Math.pow(1 - smoothing, frames));
    return true;
  }
}

class ParallaxGrid extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.stage = this.querySelector('.parallax-grid__stage');
    this.items = Array.from(this.querySelectorAll('[data-parallax-item]'));
    this.desktopMq = window.matchMedia(DESKTOP_QUERY);
    this.pointerMq = window.matchMedia(POINTER_QUERY);
    this.frame = null;
    this.lastTime = 0;
    this.inView = false;

    this.tick = this.tick.bind(this);
    this.start = this.start.bind(this);
    this.reset = this.reset.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);

    this.observer = new IntersectionObserver((entries) => {
      this.inView = entries[0].isIntersecting;
      if (this.inView) this.start();
    });

    this.observer.observe(this);
    this.classList.add('parallax-grid--animated');
    this.reset();

    window.addEventListener('scroll', this.start, { passive: true });
    window.addEventListener('resize', this.start);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerleave', this.onPointerLeave);
    this.desktopMq.addEventListener('change', this.reset);
  }

  disconnectedCallback() {
    if (!this.observer) return;

    this.observer.disconnect();
    window.cancelAnimationFrame(this.frame);
    window.removeEventListener('scroll', this.start);
    window.removeEventListener('resize', this.start);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    this.desktopMq.removeEventListener('change', this.reset);
  }

  onPointerMove(event) {
    if (!this.desktopMq.matches || !this.pointerMq.matches) return;

    const rect = this.getBoundingClientRect();
    this.mouse.x.target = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y.target = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    this.start();
  }

  onPointerLeave() {
    this.mouse.x.target = 0;
    this.mouse.y.target = 0;
    this.start();
  }

  // Read the scroll position into the targets. Mobile measures from the stage
  // (never transformed) plus offsetTop, which ignores transforms — reading each
  // item's own rect would include the offset we just applied.
  measure() {
    const viewport = window.innerHeight;

    if (this.desktopMq.matches) {
      const rect = this.getBoundingClientRect();
      const [target] = this.targets;
      target.progress.target = clamp((viewport - rect.top) / viewport);
      target.scroll.target = clamp((viewport - rect.top) / (viewport + rect.height));
      return;
    }

    const stageTop = this.stage.getBoundingClientRect().top;
    this.targets.forEach(({ element, progress, scroll }) => {
      const top = stageTop + element.offsetTop;
      progress.target = clamp((viewport - top) / (viewport * 0.5));
      scroll.target = clamp((viewport - top) / (viewport + element.offsetHeight));
    });
  }

  write() {
    this.targets.forEach(({ element, progress, scroll }) => {
      element.style.setProperty(PROGRESS_PROPERTY, progress.current.toFixed(4));
      element.style.setProperty(SCROLL_PROPERTY, scroll.current.toFixed(4));
    });
    this.style.setProperty('--pg-mouse-x', this.mouse.x.current.toFixed(4));
    this.style.setProperty('--pg-mouse-y', this.mouse.y.current.toFixed(4));
  }

  tick(time) {
    // Cap the step so a dropped or backgrounded frame doesn't jump.
    const frames = this.lastTime ? Math.min((time - this.lastTime) / (1000 / 60), 4) : 1;
    this.lastTime = time;

    if (this.inView) this.measure();

    let moving = false;
    this.targets.forEach(({ progress, scroll }) => {
      moving = progress.step(SCROLL_SMOOTHING, frames) || moving;
      moving = scroll.step(SCROLL_SMOOTHING, frames) || moving;
    });
    moving = this.mouse.x.step(MOUSE_SMOOTHING, frames) || moving;
    moving = this.mouse.y.step(MOUSE_SMOOTHING, frames) || moving;

    this.write();

    // Keep running until everything has caught up, then sleep until the next
    // scroll, resize or pointer move.
    this.frame = moving ? window.requestAnimationFrame(this.tick) : null;
    if (!moving) this.lastTime = 0;
  }

  start() {
    if (this.frame) return;
    if (!this.inView && !this.isSettling()) return;
    this.frame = window.requestAnimationFrame(this.tick);
  }

  isSettling() {
    return this.mouse.x.current !== this.mouse.x.target || this.mouse.y.current !== this.mouse.y.target;
  }

  // Switching between desktop and mobile moves the values between the section
  // and its items, so clear both, then jump straight to the current position —
  // no easing in from 0 on load.
  reset() {
    window.cancelAnimationFrame(this.frame);
    this.frame = null;

    [this, ...this.items].forEach((element) => {
      element.style.removeProperty(PROGRESS_PROPERTY);
      element.style.removeProperty(SCROLL_PROPERTY);
    });

    const elements = this.desktopMq.matches ? [this] : this.items;
    this.targets = elements.map((element) => ({
      element,
      progress: new Smoothed(),
      scroll: new Smoothed(0.5),
    }));
    this.mouse = { x: new Smoothed(), y: new Smoothed() };

    this.measure();
    this.targets.forEach(({ progress, scroll }) => {
      progress.current = progress.target;
      scroll.current = scroll.target;
    });
    this.write();
  }
}

if (!customElements.get('parallax-grid')) {
  customElements.define('parallax-grid', ParallaxGrid);
}
